/**
 * portal/Dashboard.js — "Your next steps"
 *
 * Built on the GOV.UK "Complete multiple tasks" pattern
 * (design-system.service.gov.uk/patterns/complete-multiple-tasks):
 *   - one clear NEXT STEP at the top,
 *   - every task grouped into short numbered sections,
 *   - each task shows exactly one status in words (Completed, In review,
 *     Action needed, Not started, Cannot start yet, Optional),
 *   - tasks that can't be started yet are visible but not links, with a
 *     one-line reason — so members always know what's coming.
 * The list itself is computed on the server (GET /api/portal/tasks), from
 * the same rules that lock/unlock the portal navigation.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiOutlineArrowRight, HiOutlineBell, HiOutlineChatBubbleLeftRight, HiOutlineAcademicCap,
  HiOutlineBookOpen, HiOutlineHeart, HiOutlineUser, HiOutlineCheck, HiOutlineClock,
  HiOutlineExclamationCircle, HiOutlineSparkles, HiOutlineLifebuoy, HiOutlineChevronRight,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./Dashboard.css";

const ease = [0.22, 1, 0.36, 1];

const STATUS = {
  completed:     { label: "Completed",        cls: "done" },
  in_review:     { label: "In review",        cls: "review" },
  action_needed: { label: "Action needed",    cls: "action" },
  not_started:   { label: "Not started",      cls: "todo" },
  cannot_start:  { label: "Cannot start yet", cls: "locked" },
  optional:      { label: "Optional",         cls: "optional" },
};

const QUICK_LINKS = [
  { to: "/portal/notifications", Icon: HiOutlineBell, label: "Messages", sub: "Notes from the Forge team", key: "messages" },
  { to: "/portal/community", Icon: HiOutlineChatBubbleLeftRight, label: "Community", sub: "Talk with fellow members" },
  { to: "/portal/courses", Icon: HiOutlineAcademicCap, label: "Courses", sub: "Prepare at your own pace" },
  { to: "/portal/resources", Icon: HiOutlineBookOpen, label: "Resources", sub: "Guides and documents" },
  { to: "/portal/donations", Icon: HiOutlineHeart, label: "Donations", sub: "Receipts and giving history" },
  { to: "/portal/profile", Icon: HiOutlineUser, label: "Profile", sub: "Details and preferences" },
];

const daysLeft = (d) => (d ? Math.ceil((new Date(d).getTime() - Date.now()) / 86400000) : null);

function StatusTag({ status }) {
  const s = STATUS[status] || STATUS.not_started;
  return <strong className={`dash__tag dash__tag--${s.cls}`}>{s.label}</strong>;
}

function TaskRow({ task }) {
  const linkable = task.href && task.status !== "cannot_start";
  const external = linkable && !task.href.startsWith("/portal");
  const body = (
    <>
      <span className="dash__task-main">
        <span className="dash__task-title">{task.title}</span>
        {task.hint && <span className="dash__task-hint">{task.hint}</span>}
      </span>
      <StatusTag status={task.status} />
      {linkable && <HiOutlineChevronRight className="dash__task-chev" aria-hidden="true" />}
    </>
  );
  if (!linkable) return <li className="dash__task dash__task--static">{body}</li>;
  return (
    <li className="dash__task">
      {external ? (
        <a href={task.href} className="dash__task-link">{body}</a>
      ) : (
        <Link to={task.href} className="dash__task-link">{body}</Link>
      )}
    </li>
  );
}

function NextStep({ next, offerExpiresAt }) {
  if (!next) {
    return (
      <div className="dash__next dash__next--calm">
        <div className="dash__next-icon"><HiOutlineCheck /></div>
        <div className="dash__next-body">
          <p className="dash__next-eyebrow">You're up to date</p>
          <h2 className="dash__next-title">Nothing needs your attention right now</h2>
          <p className="dash__next-hint">We'll email you as soon as there's something new to do.</p>
        </div>
      </div>
    );
  }
  const waiting = next.status === "in_review";
  const left = next.key === "book" ? daysLeft(offerExpiresAt) : null;
  const href = next.href;
  const external = href && !href.startsWith("/portal");
  const Cta = () =>
    !href || waiting ? null : external ? (
      <a href={href} className="dash__next-cta">{next.cta || "Start"} <HiOutlineArrowRight /></a>
    ) : (
      <Link to={href} className="dash__next-cta">{next.cta || "Start"} <HiOutlineArrowRight /></Link>
    );
  return (
    <div className={`dash__next ${waiting ? "dash__next--calm" : ""}`}>
      <div className="dash__next-icon">{waiting ? <HiOutlineClock /> : next.key === "book" ? <HiOutlineSparkles /> : <HiOutlineArrowRight />}</div>
      <div className="dash__next-body">
        <p className="dash__next-eyebrow">{waiting ? "With our team" : "Your next step"}</p>
        <h2 className="dash__next-title">{next.title}</h2>
        {next.hint && <p className="dash__next-hint">{next.hint}</p>}
        {left !== null && left >= 0 && (
          <p className="dash__next-deadline"><HiOutlineClock /> {left === 0 ? "Last day to book" : `${left} day${left !== 1 ? "s" : ""} left to book`}</p>
        )}
      </div>
      <Cta />
    </div>
  );
}

export default function Dashboard() {
  const [me, setMe] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([portalAPI.me(), portalAPI.tasks()])
      .then(([m, t]) => { setMe(m?.data || null); setTasks(t?.data || null); })
      .catch(() => setError("We couldn't load your next steps. Please refresh the page."));
  }, []);

  const pct = tasks?.total ? Math.round((tasks.completed / tasks.total) * 100) : 0;
  const unread = tasks?.unansweredMessages || 0;

  return (
    <PortalLayout
      eyebrow="Welcome back,"
      pageTitle={me?.firstName || "…"}
      quote="Alone we can do so little. Together we can do so much."
      quoteAuthor="Helen Keller"
    >
      <p className="dash__subtitle">
        {me?.appRef ? `${me.category} · Ref ${me.appRef}` : "Here's everything between you and your retreat, one step at a time."}
      </p>

      {error && (
        <div className="dash__error"><HiOutlineExclamationCircle /> {error}</div>
      )}

      {!tasks && !error && (
        <div className="dash__skeleton" aria-busy="true">
          <div className="dash__sk dash__sk--hero" />
          <div className="dash__sk" /><div className="dash__sk" /><div className="dash__sk" />
        </div>
      )}

      {tasks && (
        <div className="dash__grid">
          <div className="dash__main">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease }}>
              <NextStep next={tasks.next} offerExpiresAt={tasks.offerExpiresAt} />
            </motion.div>

            {unread > 0 && (
              <Link to="/portal/notifications" className="dash__alert">
                <HiOutlineBell />
                <span>
                  <strong>You have {unread} unanswered message{unread !== 1 ? "s" : ""}</strong> from the Forge team.
                </span>
                <HiOutlineArrowRight className="dash__alert-arrow" />
              </Link>
            )}

            <section className="pc dash__tasks" aria-labelledby="dash-tasks-title">
              <div className="dash__tasks-head">
                <h2 id="dash-tasks-title" className="dash__tasks-title">Your steps to retreat</h2>
                <p className="dash__progress-text">
                  You've completed <strong>{tasks.completed}</strong> of <strong>{tasks.total}</strong> steps.
                </p>
                <div className="dash__progress" role="progressbar" aria-label="Steps completed" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <motion.span className="dash__progress-fill" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8, ease }} />
                </div>
              </div>

              <ol className="dash__sections">
                {tasks.sections.map((sec, i) => (
                  <li key={sec.key} className="dash__section">
                    <h3 className="dash__section-title"><span className="dash__section-num">{i + 1}.</span> {sec.title}</h3>
                    <ul className="dash__task-list">
                      {sec.tasks.map((t) => <TaskRow key={t.key} task={t} />)}
                    </ul>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          <aside className="dash__aside">
            <div className="pc dash__links">
              <p className="p-section-title">Go to</p>
              <ul className="dash__links-list">
                {QUICK_LINKS.map((l) => {
                  const locked = !!me?.access?.locks?.[l.to.split("/")[2]];
                  return (
                    <li key={l.to}>
                      <Link to={l.to} className={`dash__link ${locked ? "dash__link--locked" : ""}`}>
                        <span className="dash__link-icon"><l.Icon /></span>
                        <span className="dash__link-text">
                          <span className="dash__link-label">
                            {l.label}
                            {l.key === "messages" && unread > 0 && <span className="dash__link-badge">{unread}</span>}
                          </span>
                          <span className="dash__link-sub">{l.sub}</span>
                        </span>
                        <HiOutlineChevronRight className="dash__link-chev" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="pc dash__help">
              <HiOutlineLifebuoy className="dash__help-icon" />
              <div>
                <p className="dash__help-title">Stuck on a step?</p>
                <p className="dash__help-sub">Reply to any message from our team, or email us — a real person reads every one.</p>
                <a href="mailto:support@forge.ngo" className="dash__help-link">support@forge.ngo</a>
              </div>
            </div>
          </aside>
        </div>
      )}
    </PortalLayout>
  );
}
